/*
 * MenuPage - initializes interactive components on Menu page, that is:
 * - Menu dropdowns and quantity badges
 * - Cart
 * - Product Card
 *
 * These components require common data (restaurant menu, ordering availability), therefore are initialized together.
 *
 * We detect if we are on Menu page by the presence of a `[data-cart]` element.
 */
import I18n from "i18n-js";
import { reaction } from "mobx";
import { isValidIntegerId, LocalStorage, PProductCardCreatorInput, RestaumaticClient, Tracking, } from "restaumatic-client";
import * as QrWaiter from "../services/QrWaiter";
import { getDataBySelector } from "../utils/getDataBySelector";
import { getMobileAppType } from "../utils/getMobileAppType";
import { measure } from "../utils/measure";
import { setCreatorHandler, updateModalFromHash } from "../utils/Modals";
import * as Cart from "./Cart/init";
import { MenuIntegration } from "./Menu/Integration";
import * as ProductCard from "./ProductCard/init";
import { findMenuItemButton } from "../utils/findMenuItemButton";
function init() {
    var _a;
    console.log("Initializing new Cart");
    const menuIntegration = new MenuIntegration();
    menuIntegration.tryShowFirstMenuSectionEarly();
    const menuPath = getDataBySelector("data-cart-menu-path", "cartMenuPath");
    const rawRestaurantId = getDataBySelector("data-cart-restaurant-id", "cartRestaurantId");
    const restaurantId = rawRestaurantId !== null ? parseInt(rawRestaurantId) : null;
    if (menuPath === null) {
        console.log("Menu path not found. Please check restaurant status");
        return;
    }
    if (restaurantId === null || !isValidIntegerId(restaurantId)) {
        console.log("Restaurant id not found or invalid. Please check restaurant status");
        return;
    }
    const client = RestaumaticClient.create({
        config: {
            siteId: Skubacz.configuration.site_id,
            locale: I18n.currentLocale(),
            fallbackLocale: I18n.defaultLocale,
            currency: Skubacz.configuration.currency,
            isApplePaySupported: window.ApplePaySession !== undefined,
            country: Skubacz.configuration.country,
            isIosReviewMode: (_a = Skubacz.configuration.ios_review_mode) !== null && _a !== void 0 ? _a : false,
            mobileAppType: getMobileAppType(),
        },
        rpcUrl: "",
        qrWaiter: QrWaiter,
        storage: LocalStorage.create(),
    });
    Tracking.trackEvent({
        tag: "VisitPage",
        contents: { page: { tag: "Menu" } },
    });
    void client
        .createCartSites({ menuPath, restaurantId })
        .then((cartIntegration) => {
        const { cart } = cartIntegration;
        const openProductCard = ProductCard.init(cartIntegration);
        const cartState = new Cart.CartState({
            cartIntegration,
            openProductCard,
        });
        const openProductCreator = (productInstance, element) => {
            if (cartIntegration.orderingAvailability.orderingImpossible) {
                Tracking.trackEvent({ tag: "OrderingImpossibleAlert" });
                window.alert(cartIntegration.orderingAvailability.reason);
                return;
            }
            const productCard = cartIntegration.createProductCard(PProductCardCreatorInput.addFromMenu(productInstance));
            openProductCard(productCard, element);
        };
        measure("initialize_client_menu", function () {
            menuIntegration.initializeElements(cartIntegration.menuIntegrationContext, openProductCreator);
        });
        measure("subscribe_to_cart", function () {
            reaction(() => cart.grandTotal, (grandTotal) => {
                document
                    .querySelectorAll("[data-cart='grandTotal']")
                    .forEach((el) => {
                    el.textContent = grandTotal !== null && grandTotal !== void 0 ? grandTotal : "-"; // null is only if the price is not a fixed value (eg. range). In this case, we should not display the element with grandTotal (see 'canOrder' below). As a placeholder we use '-'
                });
            }, { fireImmediately: true });
            reaction(() => cart.isReadyForCheckout, (isReadyForCheckout) => {
                document
                    .querySelectorAll("[data-cart='canOrder']")
                    .forEach((el) => {
                    el.classList.toggle("show", isReadyForCheckout);
                    el.classList.toggle("hide", !isReadyForCheckout);
                });
            }, { fireImmediately: true });
            // Show button only when cart is not empty
            const toggleStickyCartButton = (itemCount) => {
                const stickyCartButton = document.getElementById("sticky-cart-button");
                if (!stickyCartButton)
                    return;
                if (itemCount > 0) {
                    stickyCartButton.classList.add("is-active");
                }
                else {
                    stickyCartButton.classList.remove("is-active");
                }
            };
            reaction(() => cart.numItems, (numItems) => {
                document
                    .querySelectorAll("[data-cart='item_count']")
                    .forEach((el) => {
                    el.innerText = numItems.toString();
                    toggleStickyCartButton(numItems);
                });
            }, { fireImmediately: true });
            reaction(() => cart.price, (price) => {
                document
                    .querySelectorAll("[data-cart='item_price']")
                    .forEach((el) => {
                    el.innerText = price !== null && price !== void 0 ? price : "";
                });
            }, { fireImmediately: true });
        });
        Cart.init(cartState);
        // TODO: remove this!!!
        Skubacz.CartHooks = {
            restaurant: function () {
                return cartIntegration.menuResponse;
            },
        };
        document
            .querySelectorAll(".js-cart-button")
            .forEach((el) => {
            el.addEventListener("click", (e) => {
                e.preventDefault();
                cartState.showModal(el);
            });
        });
        document.querySelectorAll(".js-checkout-shortcut").forEach((el) => {
            el.addEventListener("click", (e) => {
                e.preventDefault();
                cartState.goToCheckout(false);
            });
        });
        measure("loaded.restaumatic.cart", function () {
            const loadedCartEvent = new Event("loaded.restaumatic.cart");
            document.dispatchEvent(loadedCartEvent);
        });
        setCreatorHandler((params) => {
            const productInstance = cartIntegration.parseCreatorUrl(params);
            if (productInstance === null) {
                window.alert(I18n.t("client.product_card.product_not_exist"));
                return;
            }
            const element = findMenuItemButton(productInstance.productTypeIds.toString());
            openProductCreator(productInstance, element);
        });
        // Show modal if there is one in location hash
        updateModalFromHash();
    });
}
export default { init };
